import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Stack,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getOptionEntryFields } from 'services/asset';
import { ExportOptionsSet, FieldAssetType } from '../../../type';
import { uniqBy, startCase, compact } from 'lodash';
import {
  createOptionsSet,
  deleteOptionsSet,
  getAllOption,
  getOptionsSet,
  getSingleEntryFields,
  updateOptionsSet
} from 'services/option';
import { InputText } from '../../../../../components/elements/InputText';
import { ButtonCustom, ButtonOutLine } from '../../../../../components/elements/ButtonCustom';
import { DialogConfirm } from '../../../../../components/elements/DialogConfirm';
import { InputSelect } from '../../../../../components/elements/InputSelect';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';
import { DropdownValueOption } from 'hooks/useGetOptionStartInspection';
import { TSingleEntryField } from '../../../../OptionsPage/hook';
import { CustomRadioGroup } from '../../../../../components/elements/RadioCustom';

// Available export columns
const AVAILABLE_COLUMNS = [
  'Asset ID',
  'Asset Custom ID',
  'Asset Custom ID 2',
  'Location Custom ID',
  'Location Custom ID 2',
  'Asset Name',
  'Client Name',
  'Main Location',
  'Sub Location 1',
  'Sub Location 2',
  'Vertical Level',
  'Location',
  'Manufacturer',
  'Asset Type',
  'Issue',
  'Model',
  'Validated',
  'Criticality',
  'Note',
  'Other Note',
  'Deficiency Description',
  'Action By Date',
  'Action By',
  'Date Created',
  'Last Modified',
  'Last Maintenance',
  'Photos',
  'Asset Issue Photos'
];

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  maxHeight: '80vh',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[24],
  padding: theme.spacing(3),
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
}));

const ColumnGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(1),
  maxHeight: '50vh',
  overflowY: 'auto',
  marginBottom: theme.spacing(2)
}));

interface ExportColumnModalProps {
  open: boolean;
  onClose: () => void;
  onExport: (
    type: 'pdf' | 'xlsx',
    imageOption: string,
    columns: string[],
    propertiesColumns: string[],
    dropdownOptionColumns?: number[],
    singleFieldColumns?: number[]
  ) => void;
}

export const ExportColumnModal: React.FC<ExportColumnModalProps> = ({
  open,
  onClose,
  onExport
}) => {
  const { setFlash } = useFlash();
  const [selectedColumns, setSelectedColumns] = useState<string[]>(AVAILABLE_COLUMNS);
  const [entryFields, setEntryFields] = useState<FieldAssetType[]>([]);
  const [optionsSet, setOptionsSet] = useState<ExportOptionsSet[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<number | null>(null);
  const [openOptionSetName, setOpenOptionSetName] = useState<boolean>(false);
  const [optionSetName, setOptionSetName] = useState<string>('');
  const [singleFields, setSingleFields] = useState<TSingleEntryField[]>([]);
  const [dropdownOptions, setDropdownOptions] = useState<DropdownValueOption[]>([]);
  const [selectedEntryFields, setSelectedEntryFields] = useState<string[]>([]);
  const [selectedDropdownOptions, setSelectedDropdownOptions] = useState<number[]>([]);
  const [selectedSingleFields, setSelectedSingleFields] = useState<number[]>([]);
  const [imageOption, setImageOption] = useState('1st');

  const exportOptions = useMemo(() => {
    const options = optionsSet.find((item) => item.id === selectedSetId)?.options;
    return options || [];
  }, [selectedSetId, optionsSet]);

  const fetchEntryFields = useCallback(async () => {
    const data = (await getOptionEntryFields({ size: 1000 })) as {
      fields: FieldAssetType[];
    };
    setEntryFields(uniqBy(data.fields, 'name'));
  }, []);

  const fetchDropdownOptions = useCallback(async () => {
    const raw = (await getAllOption()) as { metadata: DropdownValueOption[] };
    const filtered = raw.metadata.filter((item) =>
      item.options.some(
        (op) => op.type === 'ASSET' || op.type === 'REPORT_ISSUE' || op.type === 'ROOM'
      )
    );
    setDropdownOptions(filtered);
  }, []);

  const fetchSingleFields = useCallback(async () => {
    const data = (await getSingleEntryFields()) as { singleEntryFields: TSingleEntryField[] };
    setSingleFields(data.singleEntryFields);
  }, []);

  const fetchOptionsSet = useCallback(async () => {
    const data = (await getOptionsSet()) as { exportOptionsSet: ExportOptionsSet[] };
    setOptionsSet(data.exportOptionsSet);
  }, []);

  const handleSelectAll = useCallback(() => {
    const propertiesColumns = entryFields.map((item) => item?.name);
    const dropdownOptionsColumns = dropdownOptions.map((item) => item?.name);
    const singleFieldsColumns = singleFields.map((item) => item?.name);
    setSelectedDropdownOptions(dropdownOptions.map((item) => item.id));
    setSelectedSingleFields(singleFields.map((item) => item.id));
    setSelectedEntryFields(propertiesColumns);
    setSelectedColumns([
      ...AVAILABLE_COLUMNS,
      ...propertiesColumns,
      ...dropdownOptionsColumns,
      ...singleFieldsColumns
    ]);
  }, [dropdownOptions, entryFields, singleFields]);

  useEffect(() => {
    if (open) {
      if (exportOptions.length) {
        setSelectedColumns(exportOptions);
        return;
      }
      handleSelectAll();
    }
  }, [open, exportOptions, handleSelectAll]);

  useEffect(() => {
    const options = optionsSet.find((item) => item.id === selectedSetId)?.options;
    setSelectedDropdownOptions(
      compact(
        dropdownOptions.map((item) => {
          if (options?.find((op) => op === item.name)) return item.id;
        })
      )
    );
    setSelectedSingleFields(
      compact(
        singleFields.map((item) => {
          if (options?.find((op) => op === item.name)) return item.id;
        })
      )
    );
  }, [selectedSetId, optionsSet, dropdownOptions, singleFields]);

  useEffect(() => {
    fetchEntryFields();
    fetchOptionsSet();
    fetchDropdownOptions();
    fetchSingleFields();
  }, [fetchEntryFields, fetchOptionsSet, fetchDropdownOptions, fetchSingleFields]);

  const handleCreateOptionsSet = useCallback(async () => {
    const { exportOptionsSet } = await createOptionsSet({
      name: optionSetName,
      options: selectedColumns
    });
    setOpenOptionSetName(false);
    if (exportOptionsSet?.id) {
      await fetchOptionsSet();
      setSelectedSetId(exportOptionsSet?.id);
      setFlash({ type: 'success', message: 'Create options set successful' });
    }
  }, [fetchOptionsSet, optionSetName, selectedColumns, setFlash]);
  const handleSaveOptionsSet = useCallback(async () => {
    if (!selectedSetId) return;
    await updateOptionsSet(selectedSetId, {
      name: optionSetName,
      options: selectedColumns.filter((item) => typeof item === 'string')
    });
    await fetchOptionsSet();
    setOpenOptionSetName(false);
    setFlash({ type: 'success', message: 'Save options set successful' });
  }, [fetchOptionsSet, selectedSetId, selectedColumns, optionSetName, setFlash]);
  const handleDeleteOptionsSet = useCallback(async () => {
    if (!selectedSetId) return;
    await deleteOptionsSet(selectedSetId);
    await fetchOptionsSet();
    setSelectedSetId(null);
    setFlash({ type: 'success', message: 'Delete options set successful' });
  }, [fetchOptionsSet, selectedSetId, setFlash]);
  const options = useMemo(() => {
    const arr = optionsSet.map((item) => ({ label: item.name, value: `${item.id}` }));
    return [{ label: 'Select Option Set', value: '' }, ...arr];
  }, [optionsSet]);
  const { asyncCallback: asyncHandleCreateOptionsSet } = useAsyncCallback(handleCreateOptionsSet, [
    optionSetName
  ]);
  const { asyncCallback: asyncHandleSaveOptionsSet } = useAsyncCallback(handleSaveOptionsSet, [
    selectedSetId,
    optionSetName
  ]);
  const { asyncCallback: asyncHandleDeleteOptionsSet } = useAsyncCallback(handleDeleteOptionsSet, [
    selectedSetId
  ]);

  const handleColumnToggle = (column: string) => {
    setSelectedColumns((prev) =>
      prev.includes(column) ? prev.filter((col) => col !== column) : [...prev, column]
    );
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
    setSelectedDropdownOptions([]);
    setSelectedSingleFields([]);
    setSelectedEntryFields([]);
  };

  const handleExport = (type: 'pdf' | 'xlsx') => {
    if (selectedColumns.length === 0) {
      alert('Please select at least one column to export.');
      return;
    }
    const staticColumns: string[] = [];
    selectedColumns.forEach((column) => {
      if (AVAILABLE_COLUMNS.includes(column)) {
        staticColumns.push(column);
      }
    });
    onExport(
      type,
      imageOption,
      staticColumns,
      selectedEntryFields,
      selectedDropdownOptions,
      selectedSingleFields
    );
    onClose();
  };

  const isAllSelected = selectedColumns.length === AVAILABLE_COLUMNS.length;

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <ModalContent>
          <Stack direction="row" justifyContent={'space-between'} alignItems={'center'}>
            <Typography variant="h6" component="h2" gutterBottom>
              Select Export Columns
            </Typography>

            <CustomRadioGroup
              value={imageOption || '1st'}
              onChange={(val) => setImageOption(val)}
              options={[
                { value: '1st', label: '1st Only' },
                { value: 'all', label: 'All Images' }
              ]}
            />
          </Stack>
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="end">
              <Button
                sx={{ padding: '16px 12px', height: 'fit-content' }}
                size="small"
                variant="outlined"
                onClick={handleSelectAll}
                disabled={isAllSelected}>
                Select All
              </Button>
              <Button
                sx={{ padding: '16px 12px', height: 'fit-content' }}
                size="small"
                variant="outlined"
                onClick={handleDeselectAll}
                disabled={selectedColumns.length === 0}>
                Deselect All
              </Button>
              {!!optionsSet?.length && (
                <InputSelect
                  formControlStyle={{ width: '50%' }}
                  title={'Select Option Set'}
                  value={selectedSetId}
                  onChange={(e) => {
                    setSelectedSetId(Number(e.target.value));
                    setOptionSetName(
                      optionsSet.find((item) => item.id === Number(e.target.value))?.name || ''
                    );
                  }}
                  options={options}
                />
              )}
              {!!selectedSetId && (
                <Stack flexDirection={'row'} gap={'8px'} alignItems={'center'} height={56} pl={1}>
                  <img
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setOpenOptionSetName(true);
                      setOptionSetName(
                        optionsSet.find((item) => item.id === selectedSetId)?.name || ''
                      );
                    }}
                    src={'/icons/icon_action_edit.svg'}
                    alt={`icon_action_edit`}
                  />
                  <img
                    style={{ cursor: 'pointer' }}
                    onClick={asyncHandleDeleteOptionsSet}
                    src={'/icons/icon_action_delete.svg'}
                    alt={`icon_action_delete`}
                  />
                </Stack>
              )}
            </Stack>

            <Typography variant="caption" color="text.secondary">
              {selectedColumns.length} of{' '}
              {AVAILABLE_COLUMNS.length +
                entryFields?.length +
                dropdownOptions?.length +
                singleFields?.length}{' '}
              columns selected
            </Typography>
          </Box>

          <ColumnGrid>
            {AVAILABLE_COLUMNS.map((column) => (
              <FormControlLabel
                key={column}
                control={
                  <Checkbox
                    checked={selectedColumns.includes(column)}
                    onChange={() => handleColumnToggle(column)}
                    size="small"
                  />
                }
                label={column}
                sx={{ fontSize: '0.875rem' }}
              />
            ))}
            {entryFields.map((field, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={selectedColumns.includes(field?.name || '')}
                    onChange={() => {
                      if (field.name) {
                        handleColumnToggle(field.name);
                        setSelectedEntryFields((prev) =>
                          prev.includes(field.name)
                            ? prev.filter((col) => col !== field.name)
                            : [...prev, field.name]
                        );
                      }
                    }}
                    size="small"
                  />
                }
                label={field.name}
                sx={{ fontSize: '0.875rem' }}
              />
            ))}
            {dropdownOptions.map((field, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={selectedColumns.includes(field?.name || '')}
                    onChange={() => {
                      if (field) {
                        handleColumnToggle(field.name);
                        setSelectedDropdownOptions((prev) =>
                          prev.includes(field.id)
                            ? prev.filter((col) => col !== field.id)
                            : [...prev, field.id]
                        );
                      }
                    }}
                    size="small"
                  />
                }
                label={startCase(field.name)}
                sx={{ fontSize: '0.875rem' }}
              />
            ))}
            {singleFields.map((field, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={selectedColumns.includes(field?.name || '')}
                    onChange={() => {
                      if (field) {
                        handleColumnToggle(field.name);
                        setSelectedSingleFields((prev) =>
                          prev.includes(field.id)
                            ? prev.filter((col) => col !== field.id)
                            : [...prev, field.id]
                        );
                      }
                    }}
                    size="small"
                  />
                }
                label={startCase(field.name)}
                sx={{ fontSize: '0.875rem' }}
              />
            ))}
          </ColumnGrid>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => {
                selectedSetId ? asyncHandleSaveOptionsSet() : setOpenOptionSetName(true);
              }}>
              Save Options
            </Button>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleExport('xlsx')}
              disabled={selectedColumns.length === 0}>
              Export Spreadsheet
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleExport('pdf')}
              disabled={selectedColumns.length === 0}>
              Export PDF
            </Button>
          </Stack>
        </ModalContent>
      </Modal>
      <DialogConfirm
        maxWidth={480}
        textTitle={`${selectedSetId ? 'Edit' : 'Add'} Export Options Set`}
        isOpen={openOptionSetName}
        onClose={() => {
          setOpenOptionSetName(false);
          setOptionSetName('');
        }}>
        <Stack width={'100%'} alignItems={'flex-end'}>
          <Stack width={'100%'} flexDirection={'row'} alignItems={'flex-start'} gap={'20px'}>
            <Stack style={{ width: '100%', gap: '20px' }}>
              <Stack gap={1.25}>
                <Typography variant="caption" color="text.secondary">
                  Export Options Set Name
                </Typography>
                <InputText
                  colorTitle={'#226AAA'}
                  placeholder={'Enter name'}
                  inputProps={{
                    value: optionSetName,
                    onChange: (e: { target: { value: React.SetStateAction<string> } }) => {
                      setOptionSetName(e.target.value);
                    },
                    style: { minHeight: '46px', height: '46px' }
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack
            width={'100%'}
            mt={'30px'}
            alignItems={'center'}
            justifyContent={'center'}
            flexDirection={'row'}
            gap={'20px'}>
            <ButtonOutLine
              label={'Cancel'}
              style={{ width: `calc((100% - 20px)/2)` }}
              onClick={() => {
                setOpenOptionSetName(false);
                setOptionSetName('');
              }}
              fullWidth
            />
            <ButtonCustom
              label={selectedSetId ? 'Save' : 'Add'}
              style={{ width: `calc((100% - 20px)/2)` }}
              onClick={selectedSetId ? asyncHandleSaveOptionsSet : asyncHandleCreateOptionsSet}
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogConfirm>
    </>
  );
};
